'use client'

import { Suspense, useEffect, useState } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { AuthErrorBoundaryWrapper } from '../auth-error-boundary'
import { LoginLayout } from './login-layout'
import { LoginForm } from './login-form'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { useVisitorLoginRedirect } from '@/lib/hooks/use-visitor-login-redirect'

// Componente que verifica handshake ANTES do Suspense
function HandshakeCheck({ children }: { children: React.ReactNode }) {
  const [checked, setChecked] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)

  useEffect(() => {
    // Verifica parâmetros diretamente do window.location
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const otp = urlParams.get('otp')
      const tk = urlParams.get('tk')
      const redirect = urlParams.get('redirect')

      if (otp && tk && redirect) {
        console.log('🔄 Detectados parâmetros de handshake via window.location')
        console.log('📧 OTP:', otp)
        console.log('🔑 TK:', tk)
        console.log('🔗 Redirect:', redirect)

        setIsRedirecting(true)
        const handshakeUrl = `/handshake?otp=${encodeURIComponent(otp)}&tk=${encodeURIComponent(tk)}&redirect=${encodeURIComponent(redirect)}`
        console.log('🚀 Redirecionando para:', handshakeUrl)
        window.location.href = handshakeUrl
        return
      }
    }
    setChecked(true)
  }, [])

  if (isRedirecting || !checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">
            {isRedirecting ? 'Redirecionando para autenticação...' : 'Carregando...'}
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

function LoginContent() {
  const { t } = useTranslation(['common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const isRedirectingToShowcase = useVisitorLoginRedirect()

  // Verifica erro de autenticação
  const authError = searchParams.get('auth_error')

  useEffect(() => {
    // Se tiver erro de autenticação, mostrar toast
    if (authError) {
      toast.error(t('common:error.auth.toastMessage'))
      router.replace('/', { scroll: false })
    }
  }, [authError, router, t])

  if (isRedirectingToShowcase) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <LoginLayout>
      <LoginForm redirectAfterLogin={searchParams.get('redirect')} initialEmail={searchParams.get('email')} />
    </LoginLayout>
  )
}

export function LoginPageComponent() {
  return (
    <HandshakeCheck>
      <AuthErrorBoundaryWrapper>
        <Suspense fallback={
          <div className="flex min-h-screen items-center justify-center bg-background">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        }>
          <LoginContent />
        </Suspense>
      </AuthErrorBoundaryWrapper>
    </HandshakeCheck>
  )
}
